import { IconAlertTriangle, IconX } from "@tabler/icons-react";
import { testIds } from "@/testIds";
import type { PasteChip } from "./pasteFixModel";

interface PasteFixChipProps {
  chip: PasteChip;
  /** Open Symbols with this character in the search field. */
  onLookUp: (char: string) => void;
  /** Per paste, never per session: the next paste may be the one that matters. */
  onDismiss: () => void;
}

/**
 * One chip per paste (SPEC-symbols-equations §6). It states what will happen —
 * *N pasted characters won't compile.* — and offers only what exists: a lookup
 * for a symbol, which may have a command, and nothing for script or emoji,
 * which need a font or an engine instead. (Olivia, 25 Sep.)
 */
export function PasteFixChip({ chip, onLookUp, onDismiss }: PasteFixChipProps) {
  return (
    <div
      data-testid={testIds.pasteFix.chip}
      role="status"
      className="absolute bottom-4 left-1/2 -translate-x-1/2 z-40 max-w-[520px] flex items-start gap-2.5 rounded-lg border border-[var(--lilia-border)] bg-[var(--lilia-bg)] px-3 py-2 text-[13px] text-[var(--lilia-text)] shadow-md"
    >
      <IconAlertTriangle size={16} className="mt-0.5 shrink-0 text-[var(--lilia-warning)]" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="font-medium">{chip.headline}</div>
        <div data-testid={testIds.pasteFix.chars} className="mt-0.5 text-[15px] tracking-wide" lang="und">
          {chip.chars.join(" ")}
          {chip.moreChars > 0 && (
            <span className="ml-1.5 text-[12px] text-[var(--lilia-text-muted)]">+{chip.moreChars} more</span>
          )}
        </div>
        {chip.noSymbolLine && (
          <div data-testid={testIds.pasteFix.noSymbol} className="mt-0.5 text-[var(--lilia-text-muted)]">
            {chip.noSymbolLine}
          </div>
        )}
        {chip.lookUps.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {chip.lookUps.map((ch) => (
              <button
                key={ch}
                type="button"
                data-testid={testIds.pasteFix.lookUp}
                data-char={ch}
                onClick={() => onLookUp(ch)}
                className="rounded-md border border-[var(--lilia-border)] px-2 py-0.5 text-[12px] hover:bg-[var(--lilia-surface)]"
              >
                Look up {ch}
              </button>
            ))}
          </div>
        )}
      </div>
      <button
        type="button"
        data-testid={testIds.pasteFix.dismiss}
        aria-label="Dismiss"
        onClick={onDismiss}
        className="shrink-0 rounded p-0.5 text-[var(--lilia-text-muted)] hover:text-[var(--lilia-text)]"
      >
        <IconX size={14} />
      </button>
    </div>
  );
}
